import html2canvas from "html2canvas";
import { BufferTarget, CanvasSource, getFirstEncodableVideoCodec, Mp4OutputFormat, Output, Quality } from "mediabunny";

export type Mp4RenderProgress = {
  frame: number;
  frameCount: number;
  progress: number;
  stage: "preparing" | "rendering" | "finalizing";
};

export type Mp4RenderOptions = {
  html: string;
  duration: number;
  width: number;
  height: number;
  fps: number;
  signal?: AbortSignal;
  onProgress?: (progress: Mp4RenderProgress) => void;
};

export class ClientRenderError extends Error {
  readonly code: "unsupported" | "preview-unavailable" | "aborted" | "render-failed";

  constructor(message: string, code: ClientRenderError["code"] = "render-failed", cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause });
    this.code = code;
  }
}

function assertNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new ClientRenderError("The MP4 render was cancelled.", "aborted");
}

async function waitForImages(document: Document, signal?: AbortSignal): Promise<void> {
  const images = [...document.images];
  await Promise.all(images.map(async (image) => {
    assertNotAborted(signal);
    if (image.complete && image.naturalWidth > 0) return;
    try {
      await image.decode();
    } catch {
      throw new ClientRenderError(`The image ${image.currentSrc || image.src || "asset"} could not be prepared for export.`, "preview-unavailable");
    }
  }));
}

async function drawDomFrame(root: HTMLElement, canvas: HTMLCanvasElement, width: number, height: number): Promise<void> {
  await html2canvas(root, {
    canvas,
    width,
    height,
    windowWidth: width,
    windowHeight: height,
    x: 0,
    y: 0,
    scrollX: 0,
    scrollY: 0,
    scale: 1,
    backgroundColor: null,
    useCORS: true,
    allowTaint: false,
    foreignObjectRendering: false,
    logging: false,
    onclone: (_document, clonedRoot) => {
      clonedRoot.style.transform = "none";
      clonedRoot.style.transformOrigin = "0 0";
      clonedRoot.style.width = `${width}px`;
      clonedRoot.style.height = `${height}px`;
      clonedRoot.querySelectorAll<HTMLElement>(".fc-backdrop i").forEach((element) => {
        element.style.boxShadow = "0 0 1.8cqmin rgba(255,101,119,.72)";
      });
    },
  });
}

async function seekPreview(iframe: HTMLIFrameElement, time: number, signal?: AbortSignal): Promise<void> {
  assertNotAborted(signal);
  const frameWindow = iframe.contentWindow;
  if (!frameWindow) throw new ClientRenderError("The video preview is not available.", "preview-unavailable");

  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => finish(new ClientRenderError("The preview did not produce the requested frame.", "preview-unavailable")), 5000);
    const abort = () => finish(new ClientRenderError("The MP4 render was cancelled.", "aborted"));
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameWindow || event.data?.source !== "hf-deterministic-preview" || event.data?.type !== "frame") return;
      if (Math.abs(Number(event.data.time) - time) > 1 / 1000) return;
      finish();
    };
    const finish = (error?: Error) => {
      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      signal?.removeEventListener("abort", abort);
      if (error) reject(error);
      else resolve();
    };
    window.addEventListener("message", onMessage);
    signal?.addEventListener("abort", abort, { once: true });
    frameWindow.postMessage({ source: "hf-editor-preview", type: "seek", time }, "*");
  });

  await new Promise<void>((resolve) => frameWindow.requestAnimationFrame(() => frameWindow.requestAnimationFrame(() => resolve())));
}

async function createExportFrame(html: string, width: number, height: number, signal?: AbortSignal): Promise<HTMLIFrameElement> {
  assertNotAborted(signal);
  const iframe = document.createElement("iframe");
  iframe.title = "Full-quality MP4 render source";
  iframe.setAttribute("sandbox", "allow-scripts allow-same-origin");
  Object.assign(iframe.style, { position: "fixed", left: "-100000px", top: "0", width: `${width}px`, height: `${height}px`, border: "0", opacity: "0", pointerEvents: "none" });
  const ready = new Promise<void>((resolve, reject) => {
    let requestedFullQuality = false;
    const timeout = window.setTimeout(() => finish(new ClientRenderError("The full-quality render source did not become ready.", "preview-unavailable")), 10000);
    const abort = () => finish(new ClientRenderError("The MP4 render was cancelled.", "aborted"));
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow || event.data?.source !== "hf-deterministic-preview") return;
      if (event.data?.type === "ready" && !requestedFullQuality) {
        requestedFullQuality = true;
        iframe.contentWindow?.postMessage({ source: "hf-editor-preview", type: "quality", quality: "full" }, "*");
        return;
      }
      if (requestedFullQuality && event.data?.type === "quality" && event.data?.quality === "full") finish();
    };
    const finish = (error?: Error) => {
      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      signal?.removeEventListener("abort", abort);
      if (error) reject(error);
      else resolve();
    };
    window.addEventListener("message", onMessage);
    signal?.addEventListener("abort", abort, { once: true });
  });
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
  try {
    await ready;
    return iframe;
  } catch (error) {
    iframe.remove();
    throw error;
  }
}

export async function renderPreviewToMp4(options: Mp4RenderOptions): Promise<Blob> {
  const { html, duration, width, height, fps, signal, onProgress } = options;
  assertNotAborted(signal);
  if (!("VideoEncoder" in window)) throw new ClientRenderError("This browser cannot encode MP4 video. Use a current Chrome, Edge, or Safari release.", "unsupported");

  const iframe = await createExportFrame(html, width, height, signal);
  const previewDocument = iframe.contentDocument;
  if (!previewDocument) {
    iframe.remove();
    throw new ClientRenderError("The generated HyperFrames composition is not ready for export.", "preview-unavailable");
  }
  const root = previewDocument.getElementById("root");
  if (!root) {
    iframe.remove();
    throw new ClientRenderError("The generated HyperFrames composition is not ready for export.", "preview-unavailable");
  }

  onProgress?.({ frame: 0, frameCount: Math.ceil(duration * fps), progress: 0, stage: "preparing" });
  await previewDocument.fonts.ready;
  await waitForImages(previewDocument, signal);
  assertNotAborted(signal);

  const quality = new Quality({ bitrate: 10_000_000 });
  const codec = await getFirstEncodableVideoCodec(["avc"], { width, height, quality });
  if (!codec) throw new ClientRenderError("H.264 encoding is unavailable in this browser. Enable hardware video encoding or use a current Chrome, Edge, or Safari release.", "unsupported");

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new ClientRenderError("The browser could not create the video render surface.", "unsupported");

  const target = new BufferTarget();
  const output = new Output({ format: new Mp4OutputFormat({ fastStart: "in-memory" }), target });
  const source = new CanvasSource(canvas, { codec, quality, keyFrameInterval: 2, sizeChangeBehavior: "deny" });
  const frameCount = Math.ceil(duration * fps);
  output.addVideoTrack(source, { frameRate: fps, maximumPacketCount: frameCount });
  output.setMetadataTags({ title: "Shop Motion catalogue video", artist: "Shop Motion" });

  try {
    await output.start();
    for (let frame = 0; frame < frameCount; frame += 1) {
      assertNotAborted(signal);
      const timestamp = frame / fps;
      await seekPreview(iframe, timestamp, signal);
      await drawDomFrame(root, canvas, width, height);
      await source.add(timestamp, 1 / fps, { keyFrame: frame % (fps * 2) === 0 });
      onProgress?.({ frame: frame + 1, frameCount, progress: (frame + 1) / frameCount, stage: "rendering" });
      if (frame % 5 === 0) await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
    }
    onProgress?.({ frame: frameCount, frameCount, progress: 1, stage: "finalizing" });
    source.close();
    await output.finalize();
    if (!target.buffer) throw new ClientRenderError("The browser finished encoding but did not produce an MP4 file.");
    return new Blob([target.buffer], { type: "video/mp4" });
  } catch (error) {
    if (output.state !== "finalized" && output.state !== "canceled") await output.cancel().catch(() => undefined);
    if (error instanceof ClientRenderError) throw error;
    throw new ClientRenderError(error instanceof Error ? error.message : "The browser could not render the MP4.", "render-failed", error);
  } finally {
    iframe.remove();
  }
}
